import dayjs, { Dayjs } from 'dayjs';
import { DateTime } from 'luxon';

/**
 * Customer-facing calendar days are the station's (ADR-0019 rule 2): a day an
 * instant falls on is read in the location's zone, not in UTC (the date part
 * of an ISO string) and not in whatever zone the operator's browser is set to.
 *
 * `STATION_TIME_ZONE` is the admin UI's DEFAULT zone. Since #3219 every
 * location carries its own (`location.time_zone`, served as `timeZone` /
 * `locationTimeZone`): every helper below takes a trailing optional
 * `timeZone` — the zone of the booking's, group's or asset's location,
 * resolved with `zoneOfLocation` / `useLocationTimeZone` — and falls back to
 * this constant, so a Berlin location computes exactly what it did before.
 * Tenant-wide frames (dashboard periods, CSV exports, voucher and banner
 * validity, the invoice documents list, own terms) keep the default, as on
 * the backends (ADR-0019 amendment 2026-10-13, part 2).
 *
 * `GRID_TZ` (utilizationRange.ts) and `zonedDateKey` (roomWeek.ts) are this
 * constant and `stationCalendarDay` under their older names.
 *
 * Nothing else in src/ writes the literal — `scripts/time-guard` fails if it
 * does.
 *
 * Mirrors: backend/src/core/lib/date.ts `BOOKING_TIME_ZONE`,
 * new-backend/{admin,app}/src/lib/stationTime.ts,
 * apps/end-user/src/util/stationTime.ts.
 */
export const STATION_TIME_ZONE = 'Europe/Berlin';

/**
 * The zone of a location (#3219): its stored IANA name, or the station default
 * when the payload carries none (an older server, a booking without a
 * location). A name `Intl` cannot resolve THROWS — the write path refuses one,
 * and read silently it would turn days into Invalid Date. Checked once per
 * distinct name. Mirrors: backend/src/core/lib/date.ts `zoneOf`,
 * new-backend/{admin,app}/src/lib/stationTime.ts `zoneOfLocation`,
 * apps/end-user/src/util/stationTime.ts `zoneOfLocation`.
 */
const checkedZones = new Set<string>([STATION_TIME_ZONE]);
export const zoneOfLocation = (timeZone?: string | null): string => {
    const zone = timeZone || STATION_TIME_ZONE;
    if (!checkedZones.has(zone)) {
        try {
            new Intl.DateTimeFormat('en-US', { timeZone: zone }).format(0);
        } catch {
            throw new Error(`location time zone "${zone}" is not a valid IANA time zone`);
        }
        checkedZones.add(zone);
    }
    return zone;
};

const formatters = new Map<string, Intl.DateTimeFormat>();

/**
 * An `Intl.DateTimeFormat` for `options` in `timeZone`, built once per
 * (locale, options, zone) — a module-level formatter can hold only one zone.
 */
export const zonedFormatter = (
    locale: string | undefined,
    options: Intl.DateTimeFormatOptions,
    timeZone: string = STATION_TIME_ZONE,
): Intl.DateTimeFormat => {
    const key = `${locale ?? ''}|${timeZone}|${JSON.stringify(options)}`;
    let format = formatters.get(key);
    if (!format) {
        format = new Intl.DateTimeFormat(locale, { ...options, timeZone });
        formatters.set(key, format);
    }
    return format;
};

const DAY_OPTIONS: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };

/** `YYYY-MM-DD` of the station day an instant falls on, in `timeZone` (the location's). */
export const stationCalendarDay = (instant: Date | string, timeZone: string = STATION_TIME_ZONE): string =>
    zonedFormatter('en-CA', DAY_OPTIONS, timeZone).format(typeof instant === 'string' ? new Date(instant) : instant);

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * The calendar day `days` after `day` (`YYYY-MM-DD`): pure calendar
 * arithmetic, no zone — a calendar day has none, so the step runs through UTC
 * fields, where every day is a day. Mirrors: new-backend/admin
 * `stationTime.ts` `addCalendarDays`.
 */
export const addCalendarDays = (day: string, days: number): string => {
    const [year, month, date] = day.split('-').map(Number);
    const next = new Date(Date.UTC(year, month - 1, date + days));
    return `${String(next.getUTCFullYear()).padStart(4, '0')}-${pad2(next.getUTCMonth() + 1)}-${pad2(next.getUTCDate())}`;
};

const HH_MM = /^(\d{1,2}):(\d{2})/;

/** Anything the helpers below accept as a stored or picked instant. */
type InstantInput = Dayjs | Date | string;

const asDateOrIso = (value: InstantInput): Date | string =>
    dayjs.isDayjs(value) ? value.toDate() : value;

/**
 * An instant read on the station's clock.
 *
 * A date-only string (`2026-10-22`) is a calendar day, not an instant, and is
 * read as that day at the station rather than shifted into it. Unparsable
 * input gives an invalid DateTime (`isValid === false`) instead of throwing.
 */
export const atStation = (value: Date | string, timeZone: string = STATION_TIME_ZONE): DateTime => {
    if (value instanceof Date) {
        return DateTime.fromJSDate(value, { zone: timeZone });
    }
    return DateTime.fromISO(value, { zone: timeZone });
};

/**
 * The instant of wall-clock `time` on calendar `day` AT THE STATION.
 *
 * @param day `YYYY-MM-DD`
 * @param time `HH:mm`; midnight when omitted
 * @param timeZone the location's zone; the station default when omitted
 */
export const stationInstant = (day: string, time = '00:00', timeZone: string = STATION_TIME_ZONE): Date => {
    const [year, month, date] = day.split('-').map(Number);
    const match = HH_MM.exec(time);
    const hour = match ? Number(match[1]) : 0;
    const minute = match ? Number(match[2]) : 0;
    const guess = DateTime.fromObject({ year, month, day: date, hour, minute }, { zone: timeZone });
    // A wall time the autumn change repeats exists twice. Luxon picks between
    // the two by the offset in force NOW, so the same input moved by an hour
    // once the clocks went back (25.10.2026: dateTimeUtils' DST tests turned
    // red). The earlier one is taken, whenever the code runs.
    const earlier = guess.minus({ hours: 1 });
    const repeats = earlier.offset !== guess.offset
        && earlier.hour === hour && earlier.minute === minute && earlier.day === date;
    return (repeats ? earlier : guess).toJSDate();
};

/**
 * The last instant of station day `day` (`YYYY-MM-DD`) — 23:59:59.999 on the
 * station's clock, whatever the day's length. What a "valid until" / "ends on"
 * day is saved as.
 */
export const stationDayEnd = (day: string, timeZone: string = STATION_TIME_ZONE): Date =>
    DateTime.fromJSDate(stationInstant(day, '00:00', timeZone), { zone: timeZone })
        .plus({ days: 1 })
        .minus({ milliseconds: 1 })
        .toJSDate();

/**
 * The calendar day a date picker holds, as `YYYY-MM-DD`. The admin date
 * pickers hand back midnight in the BROWSER's zone; reading the label in that
 * same zone gives the day that was actually clicked.
 */
export const pickedDay = (picked: Dayjs | Date): string => dayjs(picked).format('YYYY-MM-DD');

/**
 * The picked calendar day at wall-clock `time` on the station's clock — what a
 * date + time chosen in a booking form means. `dayjs(date).hour(h)` builds the
 * same digits in the browser's zone instead, so an operator in London storing
 * 18:00 stored 19:00 Berlin.
 *
 * Returns a Dayjs so it drops into the existing form state and payload types;
 * it serialises (`toISOString` / `JSON.stringify`) as the station instant. Do
 * not read `.hour()` / `.format()` off it for display — that is the browser's
 * zone again; use `formatAtStation` / `stationTimeOfDay`.
 */
export function stationDateTime(picked: Dayjs | Date, time?: string, timeZone?: string): Dayjs;
export function stationDateTime(
    picked: Dayjs | Date | null | undefined,
    time?: string,
    timeZone?: string,
): Dayjs | null;
export function stationDateTime(
    picked: Dayjs | Date | null | undefined,
    time = '00:00',
    timeZone: string = STATION_TIME_ZONE,
): Dayjs | null {
    return picked ? dayjs(stationInstant(pickedDay(picked), time, timeZone)) : null;
}

/**
 * The form's picked day at its picked "HH:mm" (station midnight without one),
 * or NOW when no day is picked — the fallback the booking forms' submit
 * handlers have always used for an unset date.
 */
export const pickedInstantOrNow = (
    day: Dayjs | null | undefined,
    time?: string | null,
    timeZone: string = STATION_TIME_ZONE,
): Dayjs => stationDateTime(day, time || '00:00', timeZone) ?? dayjs();

/**
 * The value a date picker should hold for a stored instant: local midnight of
 * the STATION day the instant falls on. `dayjs(instant)` shows the browser's
 * day, which turned a 22.10. check-in into 21.10. for an operator in London.
 */
export function stationPickerDay(instant: InstantInput, timeZone?: string): Dayjs;
export function stationPickerDay(instant: InstantInput | null | undefined, timeZone?: string): Dayjs | null;
export function stationPickerDay(
    instant: InstantInput | null | undefined,
    timeZone: string = STATION_TIME_ZONE,
): Dayjs | null {
    return instant ? dayjs(stationCalendarDay(asDateOrIso(instant), timeZone)) : null;
}

/**
 * FOR DATE/TIME PICKERS ONLY: a browser-local Dayjs whose digits are the
 * station's wall clock at `instant`, so a picker bound to a stored instant
 * shows the station's day and time. Never send it — convert back with
 * `fromStationWallClock` (same zone) first.
 */
export function toStationWallClock(instant: InstantInput, timeZone?: string): Dayjs;
export function toStationWallClock(instant: InstantInput | null | undefined, timeZone?: string): Dayjs | null;
export function toStationWallClock(
    instant: InstantInput | null | undefined,
    timeZone: string = STATION_TIME_ZONE,
): Dayjs | null {
    return instant ? dayjs(atStation(asDateOrIso(instant), timeZone).toFormat("yyyy-MM-dd'T'HH:mm:ss")) : null;
}

/** The inverse of `toStationWallClock`: the picker's digits as a station instant. */
export function fromStationWallClock(wallClock: Dayjs, timeZone?: string): Dayjs;
export function fromStationWallClock(wallClock: Dayjs | null, timeZone?: string): Dayjs | null;
export function fromStationWallClock(wallClock: Dayjs | null, timeZone: string = STATION_TIME_ZONE): Dayjs | null {
    return wallClock ? stationDateTime(wallClock, wallClock.format('HH:mm'), timeZone) : null;
}

/**
 * What an edit form sends for a picked DAY: the booking's stored instant when
 * the day is still the one it is stored on (an untouched field), otherwise
 * station midnight of the picked day. An untouched save must send back exactly
 * what is stored — a row stored at any other time of day (say, by an operator
 * abroad before the station clock was used) would otherwise count as a changed
 * stay and be re-priced at today's rates by a save that changed nothing.
 *
 * #3219: `timeZone` is the clock the booking is (re)built on — the target
 * object's location; `storedTimeZone` the clock it is stored on — the
 * booking's own location (default: the same). The stored day is read on the
 * stored clock; a move to an object on ANOTHER clock is never "untouched",
 * even on the same days, and sends midnight on the new clock.
 */
export const pickedOrStoredInstant = (
    picked: Dayjs | null | undefined,
    stored: InstantInput | null | undefined,
    timeZone: string = STATION_TIME_ZONE,
    storedTimeZone: string = timeZone,
): Dayjs =>
    picked
    && stored
    && zoneOfLocation(storedTimeZone) === zoneOfLocation(timeZone)
    && pickedDay(picked) === stationCalendarDay(asDateOrIso(stored), storedTimeZone)
        ? dayjs(stored)
        : pickedInstantOrNow(picked, undefined, timeZone);

/**
 * A newly picked day, keeping the station time of day `previous` had (station
 * midnight when there was none) — what a date picker beside a time picker
 * writes back. A cleared picker (null) stays null.
 */
export const onDayKeepingTime = (
    day: Dayjs | null,
    previous: Dayjs | null | undefined,
    timeZone: string = STATION_TIME_ZONE,
): Dayjs | null => {
    const time = previous ? stationTimeOfDay(previous, timeZone) : '00:00';
    return stationDateTime(day, time, timeZone);
};

/** `HH:mm` of an instant on the station's clock. */
export const stationTimeOfDay = (instant: InstantInput, timeZone: string = STATION_TIME_ZONE): string =>
    atStation(asDateOrIso(instant), timeZone).toFormat('HH:mm');

/**
 * Formats an instant on the station's clock.
 *
 * @param format a luxon format string (`dd.MM.yyyy HH:mm`, `dd LLL yyyy, h:mm a`)
 * @param locale for month/weekday names; `en` (dayjs' and moment's default,
 *   what these screens rendered before) rather than the browser's locale
 * @param timeZone the location's zone; the station default when omitted
 * @returns the formatted text, or `''` when the input is not a date
 */
export const formatAtStation = (
    instant: Date | string | null | undefined,
    format: string,
    locale = 'en',
    timeZone: string = STATION_TIME_ZONE,
): string => {
    if (!instant) return '';
    const zoned = atStation(instant, timeZone);
    if (!zoned.isValid) return '';
    return zoned.setLocale(locale).toFormat(format);
};
